import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col } from 'reactstrap';

import { requestDelete } from '../../../utils/requests';
import { ADMINS } from '../../../constants/userRoles';
import { validateEmail } from '../../../utils/validators';
import {
  fetchEmailTemplate,
  fetchEmailTemplateKeywords,
  updateEmailTemplate,
  uploadEmailTemplateAttachments,
  prepareEmailPreview,
} from '../../../actions/companies/apiRequests';
import { deserializeEmailTemplate } from '../../../actions/companies/serializers';
import { DEFAULT_REPORT_TYPE } from '../../../constants/defaults';
import withRoleRestrictions from '../../shared/withRoleRestrictions';
import withEulaRestrictions from '../../license/withEulaRestrictions';
import Page from '../../shared/Page';
import SaveButton from '../../shared/SaveButton';
import CancelButton from '../../shared/CancelButton';
import CompanyDropdown from '../../shared/CompanyDropdown';
import ReportTypeDropdown from '../../shared/ReportTypeDropdown';
import TextInput from '../../shared/TextInput';
import FileUpload from '../../shared/FileUpload';
import Loader from '../../shared/Loader';
import Modal from '../../shared/Modal';
import SectionTitle from '../../shared/SectionTitle';
import SectionDescription from '../../shared/SectionDescription';
import KeywordsSection from '../../shared/KeywordsSection';
import { notifyInfo, notifyError } from '../../../utils/notifier';
import { DASHBOARD, PAGE_PATHS } from '../../../constants/pathsRoots';
import { getBreadCrumbs } from '../../../utils/breadCrumbs';
import Button from '../../shared/Button';

import BodyContent from '../BodyContent';

import styles from './styles.scss';

export class EmailLayoutPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      companyId: props.companyId,
      reportType: DEFAULT_REPORT_TYPE,
      fromEmail: '',
      subject: '',
      body: '',
      attachments: [],
      errors: {},
      loading: false,
      saving: false,
      keywords: [],
      showAttachmentUploadError: false,
      showActivationModal: false,
      preview: {},
    };

    this.setError = this.setError.bind(this);
    this.changeBody = this.changeBody.bind(this);
    this.changeSubject = this.changeSubject.bind(this);
    this.changeCompany = this.changeCompany.bind(this);
    this.changeFromEmail = this.changeFromEmail.bind(this);
    this.changeReportType = this.changeReportType.bind(this);
    this.onUploadFile = this.onUploadFile.bind(this);
    this.onRemoveFile = this.onRemoveFile.bind(this);
    this.saveChanges = this.saveChanges.bind(this);
    this.hideAttachmentUploadError = this.hideAttachmentUploadError.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.togglePreviewModal = this.togglePreviewModal.bind(this);
  }

  componentDidMount() {
    const companyId = this.state.companyId;
    if (companyId) {
      this.fetchData();
    }
  }

  get actions() {
    return [
      <CancelButton key="cancel" onClick={this.onCancel} />,
      <SaveButton key="save" onClick={this.saveChanges} loading={this.state.saving} disabled={this.state.saving} />,
    ];
  }

  get isValid() {
    const { errors } = this.state;
    return !Object.values(errors).filter((value) => !!value).length;
  }

  fetchData() {
    Promise.all([this.fetchEmailTemplateKeywords(), this.fetchEmailTemplate()]);
  }

  fetchEmailTemplate() {
    const { companyId, reportType } = this.state;

    this.setState({ loading: true });

    return fetchEmailTemplate(companyId, reportType)
      .then((response) => deserializeEmailTemplate(response, companyId, reportType))
      .then((payload) => {
        this.setState({
          fromEmail: payload.fromEmail,
          subject: payload.subject,
          body: payload.body,
          attachments: payload.attachments || [],
          loading: false,
        });
      })
      .catch(() => notifyError());
  }

  fetchEmailTemplateKeywords() {
    const companyId = this.state.companyId;

    return fetchEmailTemplateKeywords(companyId)
      .then((keywords) => {
        this.setState({ keywords });
      })
      .catch(() => notifyError());
  }

  changeBody(event) {
    this.setState({
      body: event.target.value,
    });
  }

  changeSubject(value) {
    this.setState({
      subject: value,
    });
  }

  changeFromEmail(value) {
    this.setState({
      fromEmail: value,
    });
  }

  changeCompany(company) {
    const companyId = company && company.value;
    this.setState(
      {
        companyId: companyId,
      },
      () => {
        Promise.all([this.fetchEmailTemplateKeywords(), this.fetchEmailTemplate()]);
      },
    );
  }

  changeReportType(value) {
    this.setState(
      {
        reportType: value,
      },
      () => {
        Promise.all([this.fetchEmailTemplateKeywords(), this.fetchEmailTemplate()]);
      },
    );
  }

  setError(field, error) {
    if (this.state.errors[field] === error) {
      return;
    }

    this.setState((prevState) => ({
      errors: {
        ...prevState.errors,
        [field]: error,
      },
    }));
  }

  hideAttachmentUploadError() {
    this.setState({
      showAttachmentUploadError: false,
    });
  }

  onUploadFile(files = [], rejectedFiles = []) {
    const uploadedFile = files[0];
    const rejectedFile = rejectedFiles[0];

    if (!uploadedFile || rejectedFile) {
      return this.setState({
        showAttachmentUploadError: true,
      });
    }

    this.setState((prevState) => ({
      attachments: [
        ...prevState.attachments,
        {
          name: uploadedFile.name,
          link: uploadedFile.preview,
          file: uploadedFile,
        },
      ],
    }));
  }

  onRemoveFile(link) {
    this.setState((prevState) => ({
      attachments: prevState.attachments.map((attachment) => ({
        ...attachment,
        deleted: link === attachment.link ? true : attachment.deleted,
      })),
    }));
  }

  onCancel() {
    this.props.history.goBack();
  }

  saveChanges() {
    const { companyId, reportType, fromEmail, subject, body } = this.state;

    if (!this.isValid) {
      return;
    }

    this.setState({ saving: true });

    updateEmailTemplate(companyId, reportType, fromEmail, subject, body)
      .then(() => this.deleteAttachments())
      .then(() => this.uploadAttachments())
      .then(() => notifyInfo())
      .catch(() => notifyError())
      .then(() => this.setState({ saving: false }));
  }

  async uploadFilesSequentially(newFiles) {
    const { companyId, reportType } = this.state;

    for (const newFile of newFiles) {
      await uploadEmailTemplateAttachments(companyId, reportType, newFile);
    }
  }

  uploadAttachments() {
    const { companyId, reportType, attachments = [] } = this.state;
    const newFiles = attachments.filter(({ uploaded, deleted }) => !uploaded && !deleted).map(({ file }) => file);

    if (!newFiles.length) {
      return Promise.resolve();
    }

    return this.uploadFilesSequentially(newFiles)
      .then(() => fetchEmailTemplate(companyId, reportType))
      .then((response) => deserializeEmailTemplate(response, companyId, reportType))
      .then(({ attachments }) => this.setState({ attachments }));
  }

  deleteAttachments() {
    const attachments = this.state.attachments || [];
    const deletedAttachments = attachments.filter(({ uploaded, deleted }) => uploaded && deleted);

    const deleteRequests = deletedAttachments.reduce((promiseChain, attachment) => {
      return promiseChain.then(() => requestDelete(`${attachment.link}/Delete`));
    }, Promise.resolve());

    return deleteRequests.then(() => {
      this.setState((prevState) => ({
        attachments: prevState.attachments.filter(({ deleted }) => !deleted),
      }));
    });
  }

  render() {
    const { companyId, reportType, loading } = this.state;
    const { isSpruceAdmin, isSubStandard, location } = this.props;
    const actions = companyId ? this.actions : [];

    return (
      <Page
        header="Email Templates"
        actions={actions}
        breadCrumbs={getBreadCrumbs('Email Templates', location.pathname, [PAGE_PATHS[DASHBOARD]])}
      >
        <Row className=" mb-5">
          <If condition={isSpruceAdmin}>
            <Col md={6} className="mb-3">
              <SectionTitle title="Company" />
              <SectionDescription />
              <CompanyDropdown value={companyId} onChange={this.changeCompany} />
            </Col>
          </If>
          <If condition={companyId}>
            <Col md={6} className="mb-3">
              <SectionTitle title="Report Type" />
              <SectionDescription />
              <ReportTypeDropdown value={reportType} onChange={this.changeReportType} standardSub={isSubStandard} />
            </Col>
          </If>
        </Row>
        <Choose>
          <When condition={loading}>
            <Loader />
          </When>
          <When condition={!companyId}>
            <div className="text-center my-5 py-5">No company selected</div>
          </When>
          <Otherwise>{this.renderEmailTemplate()}</Otherwise>
        </Choose>
        {this.renderAttachmentUploadErrorModal()}
        {this.renderPreviewModal()}
      </Page>
    );
  }

  renderEmailTemplate() {
    const { companyId, reportType, keywords } = this.state;
    const description = `You can use the keywords below in both body and subject. Sample values will be replaced with the real test info.`;

    return (
      <Row>
        <Col lg={8}>
          <Col className="px-0 mb-3">{this.renderFromEmail()}</Col>
          <Col className="px-0 mb-3">{this.renderSubject()}</Col>
          <Col className="px-0  mb-3">
            {this.renderBodyContent()}
            <div className={styles.preview}>
              <Button
                onClick={() => {
                  this.showPreview(companyId, reportType);
                }}
              >
                Preview
              </Button>
            </div>
          </Col>
          <Col className="px-0">
            <div className={styles.attachmentBlock}>{this.renderAttachments()}</div>
          </Col>
        </Col>
        <Col lg={4}>
          <KeywordsSection description={description} data={keywords} />
        </Col>
      </Row>
    );
  }

  renderFromEmail() {
    const { fromEmail, errors } = this.state;
    const description = 'Customer replies to report emails will be directed to this address.';

    return [
      <SectionTitle key="title" title="Reply Address" />,
      <SectionDescription key="description" description={description} />,
      <TextInput
        key="value"
        value={fromEmail}
        allowBlank={false}
        validator={validateEmail}
        validationError={'Invalid email'}
        onChange={this.changeFromEmail}
        onValidate={(error) => this.setError('fromEmail', error)}
        invalid={!!errors.fromEmail}
        error={errors.fromEmail}
      />,
    ];
  }

  renderSubject() {
    const { subject, errors } = this.state;

    return [
      <SectionTitle key="title" title="Subject" />,
      <SectionDescription key="description" />,
      <TextInput
        key="value"
        value={subject}
        allowBlank={false}
        onChange={this.changeSubject}
        onValidate={(error) => this.setError('subject', error)}
        invalid={!!errors.subject}
        error={errors.subject}
      />,
    ];
  }

  renderBodyContent() {
    const { body } = this.state;

    return [
      <SectionTitle key="title" title="Body Content" />,
      <SectionDescription key="description" />,
      <BodyContent key="value" value={body} onChange={this.changeBody} />,
    ];
  }

  renderAttachments() {
    const { attachments } = this.state;
    const attachedFiles = attachments.filter((attachment) => !attachment.deleted);
    const description =
      'Files will be attached to the email in addition to the original report. Available file extensions: PDF, DOC, DOCX, JPG, GIF, PNG, TXT. Max file size: 5 MB';

    return [
      <SectionTitle key="title" title="Attachment" />,
      <SectionDescription key="description" description={description} />,
      <FileUpload
        key="attachments"
        files={attachedFiles}
        maxFiles={3}
        accept={'.pdf, .doc, .docx, .jpg, .gif, .png, .txt'}
        onUploadFile={this.onUploadFile}
        onRemoveFile={this.onRemoveFile}
      />,
    ];
  }

  renderAttachmentUploadErrorModal() {
    const { showAttachmentUploadError } = this.state;
    return (
      <Modal
        isOpen={showAttachmentUploadError}
        showCancelButton={false}
        onConfirm={this.hideAttachmentUploadError}
        header="File upload error"
      >
        File cannot be uploaded. Make sure that file extension is PDF, DOC, DOCX, JPG, GIF, PNG, TXT and its size less
        than 5Mb
      </Modal>
    );
  }

  renderPreviewModal() {
    const { showActivationModal, preview } = this.state;
    return (
      <Modal
        header={preview.subject}
        isOpen={showActivationModal}
        action={() => {}}
        onCancel={this.togglePreviewModal}
        onSuccess={this.onStatusChangeSuccess}
        onError={this.onStatusChangeError}
        showConfirmButton={false}
        fullSizeModal={true}
        cancelButtonText="Close"
        showHeaderCancel={true}
      >
        <div dangerouslySetInnerHTML={{ __html: `<div>${preview.body}<div>` }}></div>
      </Modal>
    );
  }

  showPreview() {
    const { companyId, subject, body } = this.state;

    return prepareEmailPreview(companyId, subject, body)
      .then((preview) => {
        this.togglePreviewModal();
        this.setState({ preview });
      })
      .catch(() => notifyError());
  }

  togglePreviewModal() {
    this.setState(({ showActivationModal }) => ({
      showActivationModal: !showActivationModal,
    }));
  }
}

EmailLayoutPage.propTypes = {
  history: PropTypes.object,
  isSpruceAdmin: PropTypes.string,
  companyId: PropTypes.bool,
  isSubStandard: PropTypes.bool,
  location: PropTypes.object,
};

const mapStateToProps = ({ profile }) => ({
  isSpruceAdmin: profile.isSpruceAdmin,
  companyId: profile.companyId,
  isSubStandard: profile.isSubStandard,
});

export default connect(mapStateToProps)(withEulaRestrictions(withRoleRestrictions(EmailLayoutPage, ADMINS)));



// WEBPACK FOOTER //
// src/components/emailLayout/EmailLayoutPage/index.jsx